import React, {useMemo} from 'react';
import Picture from 'components/Picture';
import {getMediaSrc} from 'core/helpers/media';
import styles from './style.module.scss';
import {Typography} from 'components/Typography';
import {IEventResponse} from 'modules/events/types';
import {Button} from 'components/Button';
import {format} from 'date-fns';
import {ru} from 'date-fns/locale';
import {formatPrice} from 'core/helpers';
import {PRICE_STYLE} from 'core/constants';
import {useEventBuyButtonConfig} from 'modules/events/hooks';

interface IEventBannerProps {
    image: {url: string; title: string};
    title: string;
    eventDate: IEventResponse['data']['eventDate'];
    eventTimeBegin: IEventResponse['data']['eventTimeBegin'];
    eventTimeEnd: IEventResponse['data']['eventTimeEnd'];
    type: IEventResponse['data']['type'];
    price: IEventResponse['data']['price'];
    isYClients: IEventResponse['data']['isYClients'];
    ycActivityId: IEventResponse['data']['ycActivityId'];
}

export const EventBanner = (props: IEventBannerProps) => {
    const {image, title, eventDate, eventTimeBegin, eventTimeEnd, type, price} = props;

    const startDateFormatted = useMemo(() => format(new Date(eventDate), 'd MMMM, EEEE |', {locale: ru}), [eventDate]);

    const priceFormatted = formatPrice(price, {style: PRICE_STYLE.currency});

    const dateStart = new Date(`${eventDate} ${eventTimeBegin}`);
    const dateEnd = eventTimeEnd ? new Date(`${eventDate} ${eventTimeEnd}`) : undefined;

    const buyButtonConfig = useEventBuyButtonConfig();

    return (
        <div className={styles.container}>
            <div className={styles.image}>
                <Picture
                    className={styles.picture}
                    // TODO: убрать в хелпер
                    sources={{
                        lg: getMediaSrc(image.url, {width: 768}),
                        sm: getMediaSrc(image.url, {width: 522}),
                        xl: getMediaSrc(image.url, {width: 1044}),
                        xs: getMediaSrc(image.url, {width: 320}),
                    }}
                    src={getMediaSrc(image.url, {width: 1044})}
                    alt={image.title}
                />
            </div>
            <div className={styles.info}>
                <div className={styles.metaEventDateInfo}>
                    <div itemProp={'startDate'}>{dateStart?.toISOString()}</div>
                    <div itemProp={'endDate'}>{dateEnd?.toISOString()}</div>
                </div>
                <Typography variant={'label-16-r'} className={styles.time} color={'white100'}>
                    {startDateFormatted} {eventTimeBegin}
                </Typography>
                <Typography variant={'label-16-r'} className={styles.time} color={'white100'}>
                    {type}
                </Typography>
                <Typography
                    element={'h1'}
                    variant={'ch-display-42-reg'}
                    className={styles.mainHeading}
                    color={'white100'}
                >
                    {title}
                </Typography>
                <Typography variant={'label-16-r'} className={styles.description} color={'white100'}>
                    Длительность — 1,5-2 часа. Дегустации доступны гостям старше 18 лет.
                </Typography>
                <div className={styles.actions}>
                    <meta itemProp={'priceCurrency'} content={'RUB'} />
                    <link itemProp={'availability'} href={'https://schema.org/InStock'} />
                    <Button
                        className={styles.buyButton}
                        textClassName={styles.buyButtonText}
                        backColor={'white'}
                        isStretched={false}
                        {...buyButtonConfig}
                    >
                        <span className={styles.textContent}>Купить билеты</span>
                        <span className={styles.price}>{priceFormatted}</span>
                    </Button>
                    <Typography variant={'price-24-b'} className={styles.priceMobile} color={'white100'}>
                        {priceFormatted}
                    </Typography>
                    <Typography variant={'label-14-r'} className={styles.caption} color={'white-a70'}>
                        Оплачивая участие в мероприятии, вы соглашаетесь с{' '}
                        <a
                            href={
                                'https://static.simplewine.ru/upload/medialibrary/8e8/8e862ce9d55f82db85b6c10389991094.pdf'
                            }
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                            className={styles.offerLink}
                        >
                            условиями оферты
                        </a>
                        . <br className={'d-block-up-md'} />
                        Оплатить мероприятие через СБП или с карты любого банка.
                        <br /> Стоимость указана за одного человека.
                    </Typography>
                </div>
            </div>
        </div>
    );
};
